import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { TenantRole } from './types/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: TenantRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user, currentTenant } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading authentication...</div>;
  }

  if (!isAuthenticated) {
    // dopo il login si torna qui: chi arriva dal sito della scuola per
    // comprare i ticket non deve ritrovarsi nella home
    const destinazione = location.pathname + location.search;
    return <Navigate to={destinazione === '/' ? '/login' : `/login?next=${encodeURIComponent(destinazione)}`} replace />;
  }

  // Il barista entra solo dove il ruolo e' dichiarato esplicitamente: le rotte
  // aperte a chiunque sia autenticato (eventi, voli, biglietti) non lo
  // riguardano, e senza questo controllo ci arriverebbe scrivendo l'indirizzo.
  // Se il modulo bar e' spento non c'e' una cassa dove mandarlo: resta il
  // profilo, che e' aperto a tutti, invece di un rimbalzo su una pagina in 403.
  if (
    currentTenant?.role === 'barman' &&
    !user?.isPlatformAdmin &&
    !allowedRoles?.includes('barman')
  ) {
    return <Navigate to={currentTenant.barEnabled ? '/admin/bar' : '/profile'} replace />;
  }

  // L'amministratore di piattaforma passa ovunque, come gia' fa il server:
  // il suo ruolo dentro un singolo tenant puo' essere qualsiasi cosa (spesso
  // "operator"), e senza questo si vedeva la voce di menu ma cliccandola si
  // finiva su "non autorizzato".
  if (allowedRoles && user && !user.isPlatformAdmin) {
       // Ensure currentTenant and its role are defined
       if (!currentTenant || !currentTenant.role) {
           return <Navigate to="/unauthorized" replace />; // User has no role in current tenant context, or no current tenant.
       }

      const hasRequiredRole = allowedRoles.includes(currentTenant.role as any);

      if (!hasRequiredRole) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  return <>{children}</>;
};

export default ProtectedRoute;
